import { useCallback, useEffect, useState } from "react";
import { chatService } from "api/chat-service";
import { MESSAGE_PAGE_LIMIT, JUMP_MESSAGE_TOP_GAP } from "../chatScrollConstants";
import {
  createDefaultPagination,
  getMessageItems,
  getPaginationData,
  mergeUniqueMessages,
} from "../chatMessageUtils";

export const useChatThread = (chatId, handleError, viewport) => {
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState(createDefaultPagination);
  const [isFetchingInitial, setIsFetchingInitial] = useState(true);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);
  const [isFetchingNewer, setIsFetchingNewer] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isJumpingToMessage, setIsJumpingToMessage] = useState(false);

  const {
    scrollContainerRef,
    topIntersectionLockRef,
    bottomIntersectionLockRef,
    topLoadIntentRef,
    bottomPaginationArmedRef,
    forwardHistoryBrowseActiveRef,
    historyBrowseActiveRef,
    pendingJumpMessageIdRef,
    previousScrollTopRef,
    paginationRef,
    requestVersionRef,
    inFlightRef,
    loadOlderMessagesRef,
    loadNewerMessagesRef,
  } = viewport;

  const mergeMessagesState = useCallback((incomingItems) => {
    if (!Array.isArray(incomingItems) || incomingItems.length === 0) return false;
    let hasNewMessages = false;
    setMessages((prev) => {
      const merged = mergeUniqueMessages(prev, incomingItems);
      hasNewMessages = merged.length > prev.length;
      return merged;
    });
    return hasNewMessages;
  }, []);

  const updatePaginationState = useCallback((incomingPagination, options = {}) => {
    if (!incomingPagination) return;
    const { preserveHistory = false } = options;
    setPagination((prev) => {
      const nextState = !preserveHistory ? { ...prev, ...incomingPagination } : {
        ...prev,
        ...incomingPagination,
        prevCursor: prev.prevCursor,
        hasPrevPage: prev.hasPrevPage,
        nextCursor: prev.nextCursor,
        hasNextPage: prev.hasNextPage,
      };
      paginationRef.current = nextState;
      return nextState;
    });
  }, [paginationRef]);

  const fetchMessagesPage = useCallback(
      async ({
        mode,
        cursor = null,
        direction = "next",
        preserveHistory = false,
        messageId = null,
        replaceMessages = false,
      } = {}) => {
        if (!chatId || !mode || inFlightRef.current[mode]) return null;
        inFlightRef.current[mode] = true;
        const requestVersion = ++requestVersionRef.current[mode];
        if (mode === "initial") setIsFetchingInitial(true);
        if (mode === "older") setIsFetchingOlder(true);
        if (mode === "newer") setIsFetchingNewer(true);
        if (mode === "polling") setIsPolling(true);
        if (mode === "jump") setIsJumpingToMessage(true);
        try {
          const response = await chatService.getChatMessages(chatId, {
            cursor,
            limit: MESSAGE_PAGE_LIMIT,
            direction,
            messageId,
          });
          if (requestVersion !== requestVersionRef.current[mode]) return null;
          const items = getMessageItems(response);
          const nextPagination = getPaginationData(response);
          let hasNewMessages = false;
          if (replaceMessages) {
            const normalizedItems = mergeUniqueMessages([], items);
            setMessages(normalizedItems);
            hasNewMessages = normalizedItems.length > 0;
          } else {
            hasNewMessages = mergeMessagesState(items);
          }
          updatePaginationState(nextPagination, { preserveHistory });
          return { hasNewMessages, pagination: nextPagination };
        } catch (error) {
          if (error?.response?.status !== 401) handleError(error);
          return null;
        } finally {
          inFlightRef.current[mode] = false;
          if (mode === "initial") setIsFetchingInitial(false);
          if (mode === "older") setIsFetchingOlder(false);
          if (mode === "newer") setIsFetchingNewer(false);
          if (mode === "polling") setIsPolling(false);
          if (mode === "jump") setIsJumpingToMessage(false);
        }
      },
      [chatId, handleError, inFlightRef, mergeMessagesState, requestVersionRef, updatePaginationState]
  );

  const runInitialMessagesFetch = useCallback(async () => {
    if (!chatId) return false;
    const response = await fetchMessagesPage({ mode: "initial" });
    return Boolean(response);
  }, [chatId, fetchMessagesPage]);

  const loadOlderMessages = useCallback(async () => {
    const page = paginationRef.current;
    if (!chatId || !page.hasPrevPage || !page.prevCursor || inFlightRef.current.older) {
      topIntersectionLockRef.current = false;
      return;
    }
    const container = scrollContainerRef.current;
    const containerTop = Math.max(container?.scrollTop ?? 0, 0);
    const anchorElement = container
      ? Array.from(container.querySelectorAll("[data-message-id]")).find(
          (element) => element.offsetTop + element.offsetHeight >= containerTop
        )
      : null;
    const anchorId = anchorElement?.getAttribute("data-message-id") ?? null;
    const anchorOffset = anchorElement ? anchorElement.offsetTop - containerTop : 0;
    const previousHeight = container?.scrollHeight ?? 0;
    const previousTop = containerTop;
    const response = await fetchMessagesPage({
      mode: "older",
      cursor: page.prevCursor,
      direction: "prev",
    });
    if (!response) {
      topIntersectionLockRef.current = false;
      return;
    }
    const restoreScrollPosition = (attempt = 0) => {
      const nextContainer = scrollContainerRef.current;
      if (!nextContainer) {
        topIntersectionLockRef.current = false;
        return;
      }
      let nextTop = null;
      if (anchorId) {
        const nextAnchor = nextContainer.querySelector(`[data-message-id="${anchorId}"]`);
        if (nextAnchor) {
          nextTop = nextAnchor.offsetTop - anchorOffset;
        }
      }
      if (!Number.isFinite(nextTop)) {
        const newHeight = nextContainer.scrollHeight;
        nextTop = previousTop + (newHeight - previousHeight);
      }
      const maxTop = Math.max(nextContainer.scrollHeight - nextContainer.clientHeight, 0);
      const clampedTop = Math.min(Math.max(nextTop, 0), maxTop);
      const delta = Math.abs(nextContainer.scrollTop - clampedTop);
      nextContainer.scrollTop = clampedTop;
      previousScrollTopRef.current = clampedTop;
      if (attempt < 2 && delta > 1) {
        requestAnimationFrame(() => restoreScrollPosition(attempt + 1));
        return;
      }
      topIntersectionLockRef.current = false;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => restoreScrollPosition());
    });
  }, [chatId, fetchMessagesPage, inFlightRef, paginationRef, previousScrollTopRef, scrollContainerRef, topIntersectionLockRef]);

  useEffect(() => {
    loadOlderMessagesRef.current = loadOlderMessages;
  }, [loadOlderMessages, loadOlderMessagesRef]);

  const loadNewerMessages = useCallback(async () => {
    const nextPagination = paginationRef.current;
    if (!chatId || !nextPagination.hasNextPage || !nextPagination.nextCursor || inFlightRef.current.newer) return;
    const container = scrollContainerRef.current;
    const containerTop = Math.max(container?.scrollTop ?? 0, 0);
    const anchorElement = container
      ? Array.from(container.querySelectorAll("[data-message-id]")).find(
          (element) => element.offsetTop + element.offsetHeight >= containerTop
        )
      : null;
    const anchorId = anchorElement?.getAttribute("data-message-id") ?? null;
    const anchorOffset = anchorElement ? anchorElement.offsetTop - containerTop : 0;
    const previousHeight = container?.scrollHeight ?? 0;
    const previousClientHeight = container?.clientHeight ?? 0;
    const previousMaxTop = Math.max(previousHeight - previousClientHeight, 0);
    const previousTop = containerTop;
    const wasPinnedToBottom = previousTop >= previousMaxTop - 2;
    const response = await fetchMessagesPage({
      mode: "newer",
      cursor: nextPagination.nextCursor,
      direction: "next",
    });
    if (!response) {
      bottomIntersectionLockRef.current = false;
      return;
    }
    if (!response.hasNewMessages) {
      bottomIntersectionLockRef.current = false;
      return;
    }
    const restoreScrollPosition = (attempt = 0) => {
      const nextContainer = scrollContainerRef.current;
      if (!nextContainer) {
        bottomIntersectionLockRef.current = false;
        return;
      }
      let nextTop = null;
      if (anchorId) {
        const nextAnchor = nextContainer.querySelector(`[data-message-id="${anchorId}"]`);
        if (nextAnchor) {
          nextTop = nextAnchor.offsetTop - anchorOffset;
        }
      }
      if (!Number.isFinite(nextTop)) {
        const newHeight = nextContainer.scrollHeight;
        const newMaxTop = Math.max(newHeight - nextContainer.clientHeight, 0);
        nextTop = wasPinnedToBottom ? newMaxTop : previousTop;
      }
      const maxTop = Math.max(nextContainer.scrollHeight - nextContainer.clientHeight, 0);
      const clampedTop = Math.min(Math.max(nextTop, 0), maxTop);
      const delta = Math.abs(nextContainer.scrollTop - clampedTop);
      nextContainer.scrollTop = clampedTop;
      previousScrollTopRef.current = clampedTop;
      if (attempt < 2 && delta > 1) {
        requestAnimationFrame(() => restoreScrollPosition(attempt + 1));
        return;
      }
      bottomIntersectionLockRef.current = false;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => restoreScrollPosition());
    });
  }, [bottomIntersectionLockRef, chatId, fetchMessagesPage, inFlightRef, paginationRef, previousScrollTopRef, scrollContainerRef]);

  useEffect(() => {
    loadNewerMessagesRef.current = loadNewerMessages;
  }, [loadNewerMessages, loadNewerMessagesRef]);

  const scrollToMessageWithRetry = useCallback((messageId, attempt = 0) => {
    const container = scrollContainerRef.current;
    if (!container || !messageId) return false;
    const targetElement = container.querySelector(`[data-message-id="${messageId}"]`);
    if (targetElement) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const relativeTop = targetRect.top - containerRect.top;
      const desiredTop = container.scrollTop + relativeTop - JUMP_MESSAGE_TOP_GAP;
      const maxTop = Math.max(container.scrollHeight - container.clientHeight, 0);
      const clampedTop = Math.min(Math.max(desiredTop, 0), maxTop);
      container.scrollTop = clampedTop;
      previousScrollTopRef.current = clampedTop;
      return true;
    }
    if (attempt >= 6) return false;
    requestAnimationFrame(() => {
      scrollToMessageWithRetry(messageId, attempt + 1);
    });
    return false;
  }, [previousScrollTopRef, scrollContainerRef]);

  const handleJumpToMessage = useCallback(
      async (messageId) => {
        if (!chatId || !messageId || inFlightRef.current.jump) return;
        topIntersectionLockRef.current = true;
        topLoadIntentRef.current = false;
        historyBrowseActiveRef.current = false;
        forwardHistoryBrowseActiveRef.current = false;
        bottomPaginationArmedRef.current = false;
        pendingJumpMessageIdRef.current = messageId;
        const response = await fetchMessagesPage({
          mode: "jump",
          messageId,
          replaceMessages: true,
        });
        if (!response) {
          pendingJumpMessageIdRef.current = null;
          topIntersectionLockRef.current = false;
          return;
        }
        const tryScrollAfterLayout = (attempt = 0) => {
          const didScroll = scrollToMessageWithRetry(messageId);
          if (didScroll) {
            pendingJumpMessageIdRef.current = null;
            if (attempt < 2) {
              requestAnimationFrame(() => tryScrollAfterLayout(attempt + 1));
              return;
            }
            topIntersectionLockRef.current = false;
            return;
          }
          if (attempt >= 6) {
            const container = scrollContainerRef.current;
            if (container) {
              container.scrollTop = container.scrollHeight;
              previousScrollTopRef.current = Math.max(container.scrollTop, 0);
            }
            pendingJumpMessageIdRef.current = null;
            topIntersectionLockRef.current = false;
            return;
          }
          requestAnimationFrame(() => tryScrollAfterLayout(attempt + 1));
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            tryScrollAfterLayout();
          });
        });
      },
      [chatId, fetchMessagesPage, scrollToMessageWithRetry, bottomPaginationArmedRef, forwardHistoryBrowseActiveRef, historyBrowseActiveRef, inFlightRef, pendingJumpMessageIdRef, previousScrollTopRef, scrollContainerRef, topIntersectionLockRef, topLoadIntentRef]
  );

  const refreshLatestMessages = useCallback(async () => {
    if (!chatId || inFlightRef.current.refresh) return;
    inFlightRef.current.refresh = true;
    try {
      await fetchMessagesPage({ mode: "refresh", preserveHistory: true });
    } finally {
      inFlightRef.current.refresh = false;
    }
  }, [chatId, fetchMessagesPage, inFlightRef]);

  const pollMessagesPage = useCallback(async () => {
    if (!chatId) return null;
    if (!bottomPaginationArmedRef.current) return null;
    return fetchMessagesPage({ mode: "polling", preserveHistory: true });
  }, [bottomPaginationArmedRef, chatId, fetchMessagesPage]);

  const resetChatTranscript = useCallback(() => {
    setMessages([]);
    const nextDefaultPagination = createDefaultPagination();
    paginationRef.current = nextDefaultPagination;
    setPagination(nextDefaultPagination);
    setIsFetchingInitial(true);
  }, [paginationRef]);

  return {
    messages,
    setMessages,
    pagination,
    setPagination,
    isFetchingInitial,
    isFetchingOlder,
    isFetchingNewer,
    isPolling,
    isJumpingToMessage,
    fetchMessagesPage,
    runInitialMessagesFetch,
    loadOlderMessages,
    loadNewerMessages,
    handleJumpToMessage,
    scrollToMessageWithRetry,
    refreshLatestMessages,
    pollMessagesPage,
    mergeMessagesState,
    updatePaginationState,
    resetChatTranscript,
  };
};
